import clsx from 'clsx';
import Link from 'next/link';

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { Loader } from 'common';
import { Icon } from 'common/icon';
import { BlurHashImage } from 'components/blurhash-image';
import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';
import { Header } from 'components/slider/header';
import { Routes } from 'constants/routes';
import { ICasino, IStreamer } from 'constants/types';
import { Tag } from './tag';

import { useFavorites } from 'hooks/useFavorites';
import { useRTPWeights } from 'hooks/useRTPWeights';
import { useCallback, useMemo } from 'react';

import { GuideItem } from 'components/guide-item';
import { StreamerCard } from 'components/streamer-card';
import { Anchor } from 'constants/anchors';
import { getItemIdentifier } from 'utils/slug';
import {
  getColorForTag,
  getIconForTag,
  getTextColorForTag,
} from 'utils/tagColor';
import styles from './styles.module.css';
import { tagsData } from './tags';
import { getCasinoRTP } from 'utils/casino';

enum RtpStatus {
  Loading = 'loading',
  Bad = 'bad',
  Good = 'good',
  Ok = 'ok',
}

export const Card = ({
  data,
  hasIcon,
  hasFavoritesActions,
  isWidthStretching,
}: {
  data?: ICasino | null;
  hasIcon?: boolean;
  hasFavoritesActions?: boolean;
  isWidthStretching?: boolean;
}) => {
  const { removeFromFavorite, isLoading } = useFavorites();
  const { isBad, isGood, isFetching: isRatingFetching } = useRTPWeights();
  const rtpValue = data ? getCasinoRTP(data).number : 0;

  const renderIcon: (rtp: number) => React.JSX.Element = useCallback(
    (rtp: number): React.JSX.Element => {
      if (isRatingFetching) {
        return <Icon.Cherry fill="#FEFEFE" />;
      }
      if (isBad(rtp)) {
        return <Icon.Bad />;
      } else if (isGood(rtp)) {
        return <Icon.Good />;
      } else {
        return <Icon.Cherry />;
      }
    },
    [isBad, isGood, isRatingFetching]
  );

  const rtpStatus = useMemo((): RtpStatus => {
    if (isRatingFetching) return RtpStatus.Loading;
    if (isBad(rtpValue)) return RtpStatus.Bad;
    if (isGood(rtpValue)) return RtpStatus.Good;
    return RtpStatus.Ok;
  }, [isBad, isGood, isRatingFetching, rtpValue]);

  const renderVerdict: (rtp: number) => React.JSX.Element = useCallback(
    (rtp: number): React.JSX.Element => {
      if (isRatingFetching) {
        return <div>Fetching...</div>;
      }
      if (isBad(rtp)) {
        return <div>Very Low RTP</div>;
      } else if (isGood(rtp)) {
        return <div>REDUCED RTP</div>;
      } else {
        return <div>HIGH RTP</div>;
      }
    },
    [isBad, isGood, isRatingFetching]
  );

  if (!data) {
    return null;
  }

  const reviewsCount = data?.casinos_reviews_count ?? 0;

  const starRating = data?.rating;
  const backgroundSrc = data?.imageUrl || data?.logoUrl || '';
  const showLogoOverlay = Boolean(data?.imageUrl && data?.logoUrl);
  const casinoId = getItemIdentifier(data);
  const casinoHref = casinoId ? Routes.casinoPage(casinoId) : null;

  const cardContent = (
    <>
      <div className={styles.casino_card__wrapper}>
        <div className={styles.casino_card__cover}>
          <div className={styles.gradient} />
          {data?.tags && (
            <div className={styles.tags}>
              {data?.tags.map((tag, index) => (
                <Tag
                  icon={getIconForTag(tag, tagsData)}
                  color={getColorForTag(tag, tagsData)}
                  text={tag}
                  key={tag + index}
                  textColor={getTextColorForTag(tag)}
                />
              ))}
            </div>
          )}

          {!hasIcon && (
            <div className={styles.casino_card__rtp}>
              {renderIcon(rtpValue)}
              <span
                className={clsx({
                  [styles.highRTP_text]: rtpStatus === RtpStatus.Loading,
                  [styles.lowRTP_text]: rtpStatus === RtpStatus.Bad,
                  [styles.goodRTP_text]: rtpStatus === RtpStatus.Good,
                  [styles.highRTP_text]: rtpStatus === RtpStatus.Ok,
                })}
              >
                {renderVerdict(rtpValue)}
              </span>
            </div>
          )}

          <LogoOverlayWrapper>
            <BlurHashImage
              alt=""
              blurhash={data?.blurHash}
              src={backgroundSrc}
              sizes="(max-width: 769px) 160px, 190px"
            />

            {showLogoOverlay && (
              <LogoOverlay alt={data?.name} image={data?.logoUrl} />
            )}
          </LogoOverlayWrapper>
        </div>
      </div>

      {hasFavoritesActions && <div className={styles.favorite_action_shadow} />}
    </>
  );

  return (
    <div
      className={clsx(styles.cards__card, styles.casino_card, {
        [styles.widthStretching]: isWidthStretching,
      })}
    >
      {casinoHref ? (
        <Link prefetch={false} href={casinoHref}>
          {cardContent}
        </Link>
      ) : (
        cardContent
      )}
      <div
        className={clsx(styles.casino_review, {
          [styles.highRTP_bg]: rtpStatus === RtpStatus.Loading,
          [styles.lowRTP_bg]: rtpStatus === RtpStatus.Bad,
          [styles.goodRTP_bg]: rtpStatus === RtpStatus.Good,
          [styles.highRTP_bg]: rtpStatus === RtpStatus.Ok,
        })}
      >
        <div className={styles.casino_rating}>
          <div className={styles.starRating}>
            <Icon.Star
              fill="black"
              fillOpacity={1}
              height={'14'}
              width={'14'}
              className={styles.starRating}
            />
          </div>
          {parseFloat(starRating)?.toFixed(2)}
        </div>
        {casinoHref ? (
          <Link
            prefetch={false}
            key={data._id}
            href={`${casinoHref}#${Anchor.CasinoReviews}`}
            className={styles.casino_review__link}
          >
            {`Read ${reviewsCount} reviews`}
            <Icon.Arrow fill={'black'} width={14} height={14} />
          </Link>
        ) : (
          <span className={styles.casino_review__link}>
            {`Read ${reviewsCount} reviews`}
          </span>
        )}
      </div>

      {hasFavoritesActions && (
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            removeFromFavorite({ favoriteId: data?._id });
          }}
          className={styles.wrapper_favorite_action}
        >
          {isLoading ? <Loader size="medium" /> : <Icon.Remove />}
        </div>
      )}
    </div>
  );
};

type Props = {
  headerId?: string;
  data?: any;
  url?: string;
  name?: string;
  isUrl?: boolean;
  title?: string;
  guides?: string[];
  container?: string;
  description?: string;
  hasFavoritesActions?: boolean;
  count?: string | number | undefined;
  starsRating?: number;
  type?: 'streamer' | 'casino' | 'slot';
  anchor?: string;
  isWidthStretching?: boolean;
};

export const CardsMedium = ({
  headerId,
  url,
  isUrl,
  data,
  name,
  count,
  title,
  guides,
  type,
  description,
  hasFavoritesActions,
  container = 'medium',
  anchor = Anchor.slot.forMoney,
  isWidthStretching,
}: Props) => {
  const containerStyles = clsx({
    [styles.container]: container === 'medium',
    [styles.container_s]: container === 'mini',
  });

  return (
    <section className={styles.cards}>
      <div className={containerStyles}>
        <div id={anchor} />
        {title && (
          <div className={styles.cards_header_container}>
            {title && <h1 className={styles.cards__title}>{title}</h1>}
            {description && (
              <h3 className={styles.cards__description}>{description}</h3>
            )}
            <div className={styles.cards_guides_container}>
              {guides?.map((id) => <GuideItem key={id} id={id} />)}
            </div>
          </div>
        )}
        {name && (
          <Header
            id={headerId}
            name={name}
            count={count}
            url={url?.length ? url : isUrl ? Routes.allCasinos : undefined}
          />
        )}
        {!!data?.length && (
          <div
            className={clsx(styles.cards__row, {
              [styles.widthStretching]: isWidthStretching,
            })}
          >
            {data?.map((item: any, index: number) =>
              type === 'streamer' ? (
                <StreamerCard
                  key={`streamer-${item?._id}-${index}`}
                  item={item as IStreamer}
                  variant={'main'}
                  isLink
                />
              ) : (
                <Card
                  data={item as ICasino}
                  key={`casino-${item?._id}-${index}`}
                  hasFavoritesActions={hasFavoritesActions}
                  isWidthStretching={isWidthStretching}
                />
              )
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export const CardSkeleton = () => {
  return (
    <div className={styles.container_skeleton}>
      <Skeleton width={190} height={270} className={styles.img_skeleton} />

      <div className={styles.wrapper_footer_skeleton}>
        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton key={index.toString()} width={190} height={20} />
        ))}
      </div>
    </div>
  );
};

export const CardsSkeleton = ({ container = 'medium' }: any) => {
  const containerStyles = clsx({
    [styles.container]: container === 'medium',
    [styles.container_s]: container === 'mini',
  });

  return (
    <div className={containerStyles}>
      <div className={styles.header_skeleton}>
        <Skeleton width={220} height={30} />
      </div>
      <div className={styles.cards__row}>
        {Array.from({ length: 6 }).map((_, index) => (
          <CardSkeleton key={index.toString()} />
        ))}
      </div>
    </div>
  );
};
